/**
 * `feature:editor_input_latency` — is typing in the SQL editor snappy?
 *
 * Measures keypress → next-paint via the Event Timing API
 * (`PerformanceObserver` type `'event'`): `entry.duration` is the elapsed
 * time from the input event's hardware timestamp until the frame that
 * follows its processing is painted — exactly the "I pressed a key and the
 * character appeared" latency users feel. Probe-verified supported in our
 * Electron (34 / Chromium 132).
 *
 * Aggregated locally per (engine, docSizeBucket) — one summary event per
 * non-empty key on each flush (10-min lazy interval, capped, + unload).
 * NEVER a per-keystroke event, and never any typed text — counts and
 * durations only.
 *
 * FROZEN definitions (changing any invalidates cross-release comparison,
 * same rule as telemetryBuckets.ts): the 24ms observer threshold, the
 * 24/50/100/250 slow edges, the doc-size buckets, and the keydown-only
 * entry filter. The filter exists because one physical keystroke yields
 * keydown+beforeinput+input entries — counting all three would triple the
 * slow counts against the Monaco onKeyDown denominator. `keydown` is also
 * the earliest anchor, so its duration covers the full press-to-paint span.
 *
 * Attribution is by INPUT time: each keystroke Monaco sees is remembered with
 * its (engine, docSizeBucket) key and its event `timeStamp`, and a delivered
 * entry counts only against the keystroke whose `timeStamp` equals its
 * `startTime` (the Event Timing spec sets one from the other; probed identical
 * in Electron 34). The observer delivers entries after the next paint, often
 * batched — attributing them to whichever editor was focused on DELIVERY put
 * slow entries in groups that never counted their keystroke (1.5.0 field
 * data: 11 slow50 over 8 keystrokes). A keydown no editor counted (grid,
 * dialog inputs) is dropped, so a group's slow counts can never exceed its
 * keystrokes.
 */

export const EDITOR_INPUT_LATENCY_EVENT = 'feature:editor_input_latency';
/** Entries shorter than this are never delivered — they are "fast enough". */
export const OBSERVER_THRESHOLD_MS = 24;
export const INPUT_LATENCY_FLUSH_INTERVAL_MS = 10 * 60 * 1000;
/**
 * Interval-flush cap per app run — only intervals that actually EMITTED count
 * (an idle editor must not burn the cap and push real data to the unload
 * flush, which a crash would lose). At the cap the interval is cleared; the
 * unload flush is always allowed.
 */
export const MAX_INTERVAL_FLUSHES = 12;

/**
 * Engine label for typing that happened in an editor with no live connection —
 * an untitled/scratch tab, a tab whose connection dropped, or the startup
 * window before session restore finishes connecting.
 *
 * These keystrokes MUST NOT be attributed to a real engine. The editor's
 * language-service default ('mssql', see `resolveEngineId` in SqlMonaco) is a
 * provider-selection fallback, not knowledge about the user's database —
 * labelling with it silently poured the jankiest phase of the app's life
 * (cold start, Monaco mounting, no language service) into one engine's
 * bucket. An explicit value keeps that volume visible and filterable instead
 * of dropping it, which would also make per-engine `keystrokes` denominators
 * non-comparable across releases.
 */
export const UNKNOWN_ENGINE = 'unknown';

export type DocSizeBucket = '0-100' | '101-1000' | '1000+';

export function bucketDocSize(lineCount: number): DocSizeBucket {
  if (lineCount <= 100) return '0-100';
  if (lineCount <= 1000) return '101-1000';
  return '1000+';
}

type LatencyAggregate = {
  keystrokes: number;
  slow24: number;
  slow50: number;
  slow100: number;
  slow250: number;
  maxMs: number;
};

type FocusOwner = symbol;

/** A keystroke still waiting for its (possibly never-delivered) Event Timing entry. */
type PendingKeystroke = { timeStamp: number; key: string };

/**
 * How many keystrokes wait for their entry. Most never get one (a keystroke
 * under the 24ms threshold is not delivered), so this is a ring, oldest
 * dropped first: 128 covers ~4s of key auto-repeat, far longer than an
 * entry's delivery delay.
 */
const PENDING_KEYSTROKE_CAPACITY = 128;
/** An entry's `startTime` matches a keystroke's `timeStamp` within this. */
const INPUT_MATCH_TOLERANCE_MS = 1;

const aggregates = new Map<string, LatencyAggregate>();
const pendingKeystrokes: PendingKeystroke[] = [];
let focusKey: string | null = null;
let focusOwner: FocusOwner | null = null;
let observer: PerformanceObserver | null = null;
let flushTimer: ReturnType<typeof setInterval> | null = null;
let intervalFlushes = 0;
let unloadHookInstalled = false;

function aggregateFor(key: string): LatencyAggregate {
  let agg = aggregates.get(key);
  if (!agg) {
    agg = { keystrokes: 0, slow24: 0, slow50: 0, slow100: 0, slow250: 0, maxMs: 0 };
    aggregates.set(key, agg);
  }
  return agg;
}

/**
 * A Monaco editor gained text focus. Returns the owner token the caller must
 * pass to `notifyEditorBlur` — blur only clears focus if the owner still
 * holds it, so an unmounting background editor can't blind the focused one.
 * The first focus lazily starts the observer + flush machinery.
 *
 * `engine` must be the engine of a LIVE connection; pass `undefined` when the
 * editor has none, and the sample is labelled {@link UNKNOWN_ENGINE}. Callers
 * must never substitute a UI default here — see the constant's note.
 *
 * Safe to call again on the same editor when its engine resolves (e.g. the
 * connection lands while the editor is already focused): the later keystrokes
 * then aggregate under the real engine instead of staying `unknown`.
 */
export function notifyEditorFocus(engine: string | undefined, lineCount: number): FocusOwner {
  focusKey = `${engine || UNKNOWN_ENGINE}|${bucketDocSize(lineCount)}`;
  focusOwner = Symbol('editor');
  ensureObserver();
  ensureFlushMachinery();
  return focusOwner;
}

export function notifyEditorBlur(owner: FocusOwner): void {
  if (focusOwner === owner) {
    focusKey = null;
    focusOwner = null;
  }
}

/**
 * Called from Monaco's onKeyDown — the denominator for the slow shares.
 * `inputTimeStamp` is the keydown event's `timeStamp`, the key its Event
 * Timing entry is matched on.
 */
export function recordKeystroke(inputTimeStamp: number): void {
  if (!focusKey) return;
  aggregateFor(focusKey).keystrokes += 1;
  pendingKeystrokes.push({ timeStamp: inputTimeStamp, key: focusKey });
  if (pendingKeystrokes.length > PENDING_KEYSTROKE_CAPACITY) pendingKeystrokes.shift();
}

/** Remove and return the aggregate key of the keystroke `startTime` belongs to, if any. */
function takeKeystrokeKey(startTime: number): string | null {
  let best = -1;
  let bestDelta = Infinity;
  for (let i = 0; i < pendingKeystrokes.length; i++) {
    const delta = Math.abs(pendingKeystrokes[i].timeStamp - startTime);
    if (delta <= INPUT_MATCH_TOLERANCE_MS && delta < bestDelta) {
      best = i;
      bestDelta = delta;
    }
  }
  return best === -1 ? null : pendingKeystrokes.splice(best, 1)[0].key;
}

/**
 * Classify delivered Event Timing entries. Exported for tests; the observer
 * callback delegates here. Each keydown entry counts against the group of
 * the keystroke it matches (see the header); an entry matching no pending
 * keystroke — typed outside an editor, or already flushed — is dropped.
 */
export function recordPerformanceEntries(
  entries: ReadonlyArray<{ name: string; startTime: number; duration: number }>,
): void {
  for (const entry of entries) {
    if (entry.name !== 'keydown') continue;
    const key = takeKeystrokeKey(entry.startTime);
    if (key === null) continue;
    const agg = aggregateFor(key);
    const d = entry.duration;
    agg.slow24 += 1;
    if (d >= 50) agg.slow50 += 1;
    if (d >= 100) agg.slow100 += 1;
    if (d >= 250) agg.slow250 += 1;
    if (d > agg.maxMs) agg.maxMs = Math.round(d);
  }
}

function ensureObserver(): void {
  if (observer) return;
  try {
    if (!PerformanceObserver.supportedEntryTypes?.includes('event')) return;
    observer = new PerformanceObserver((list) => {
      recordPerformanceEntries(list.getEntries());
    });
    // `durationThreshold` floors at 16ms per spec; 24 skips one-missed-frame
    // noise while catching anything a user could plausibly feel.
    observer.observe({ type: 'event', durationThreshold: OBSERVER_THRESHOLD_MS } as PerformanceObserverInit);
  } catch {
    observer = null; // Unsupported → module degrades to keystroke counting only; flush skips empty slows anyway.
  }
}

function ensureFlushMachinery(): void {
  if (!flushTimer && intervalFlushes < MAX_INTERVAL_FLUSHES) {
    flushTimer = setInterval(() => {
      if (flushEditorInputLatency() > 0) {
        intervalFlushes += 1;
      }
      if (intervalFlushes >= MAX_INTERVAL_FLUSHES && flushTimer) {
        clearInterval(flushTimer);
        flushTimer = null;
      }
    }, INPUT_LATENCY_FLUSH_INTERVAL_MS);
  }
  if (!unloadHookInstalled && typeof window !== 'undefined') {
    unloadHookInstalled = true;
    window.addEventListener('beforeunload', flushEditorInputLatency);
  }
}

/**
 * Emit one summary per non-empty (engine, docSizeBucket) key, then reset.
 * Returns the number of events emitted — the interval only counts emitting
 * flushes against MAX_INTERVAL_FLUSHES.
 */
export function flushEditorInputLatency(): number {
  let emitted = 0;
  for (const [key, agg] of aggregates) {
    if (agg.keystrokes === 0 && agg.slow24 === 0) continue;
    emitted += 1;
    const [engine, docSizeBucket] = key.split('|');
    window.electronAPI?.analytics
      ?.track(EDITOR_INPUT_LATENCY_EVENT, {
        engine,
        docSizeBucket,
        keystrokes: agg.keystrokes,
        slow24: agg.slow24,
        slow50: agg.slow50,
        slow100: agg.slow100,
        slow250: agg.slow250,
        maxMs: agg.maxMs,
      })
      .catch(() => {});
  }
  aggregates.clear();
  // A keystroke counted in THIS flush must not have its entry land in the
  // next window's group, which would carry the slow count without the
  // keystroke.
  pendingKeystrokes.length = 0;
  return emitted;
}

/** Test-only: reset all module state and disconnect the observer. */
export function _resetEditorInputLatencyForTests(): void {
  aggregates.clear();
  pendingKeystrokes.length = 0;
  focusKey = null;
  focusOwner = null;
  intervalFlushes = 0;
  observer?.disconnect();
  observer = null;
  if (flushTimer) {
    clearInterval(flushTimer);
    flushTimer = null;
  }
  if (unloadHookInstalled && typeof window !== 'undefined') {
    window.removeEventListener('beforeunload', flushEditorInputLatency);
    unloadHookInstalled = false;
  }
}
